import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Check, Ban, X } from "lucide-react";

const RISK_COLORS = { low: "#4ADE80", medium: "#FACC15", high: "#F87171" };
const STATUS_COLORS = { pending: "#FACC15", approved: "#4ADE80", suspended: "#F87171", rejected: "#71717A" };

export default function MerchantManager() {
  const [merchants, setMerchants] = useState(null);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => { api.get("/admin/merchants").then(({ data }) => setMerchants(data)).catch(() => {}); };
  useEffect(load, []);

  const decide = async (m, action) => {
    setBusy(`${m.id}-${action}`);
    try {
      await api.post(`/admin/merchants/${m.id}/decide`, { action });
      toast.success(`${m.business_name} ${action === "approve" ? "approved — they can now redeem for settlements" : action === "suspend" ? "suspended" : "rejected"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (!merchants) return null;
  const shown = merchants.filter((m) => !filter ||
    `${m.business_name} ${m.user_name}`.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="space-y-4" data-testid="merchant-manager">
      <p className="text-xs text-zinc-500">
        Merchants and Card Merchants must be approved here before they can redeem earned coins for settlement payouts.
        Risk scores are AI-computed from transaction history, refunds, account age, consistency, fraud indicators and volume.
      </p>
      <input data-testid="merchant-search" value={filter} onChange={(e) => setFilter(e.target.value)}
        placeholder="Search merchants…"
        className="w-full max-w-xs bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
      <div className="divide-y divide-pb-border/40 border border-pb-border" data-testid="merchant-list">
        {shown.length === 0 && <div className="p-4 text-xs text-zinc-600">No merchant applications yet — sign-ups appear here for review.</div>}
        {shown.map((m) => (
          <div key={m.id} className="px-3 py-3" data-testid={`merchant-row-${m.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[180px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-white">{m.business_name}</span>
                  <span className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 border border-pb-border text-zinc-400">
                    {m.type === "card_merchant" ? "Card Merchant" : "Merchant"}
                  </span>
                  <span className="text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 border"
                    style={{ color: STATUS_COLORS[m.status], borderColor: `${STATUS_COLORS[m.status]}66` }}
                    data-testid={`merchant-status-${m.id}`}>{m.status}</span>
                </div>
                <div className="text-[10px] text-zinc-500 mt-0.5">{m.user_name} · {m.email}{m.phone ? ` · ${m.phone}` : ""}</div>
                {m.description && <div className="text-[10px] text-zinc-600 mt-0.5 line-clamp-1">{m.description}</div>}
              </div>
              <div className="text-center">
                <div className="text-[8px] uppercase tracking-widest text-zinc-500">Risk</div>
                <div className="font-mono font-black text-sm" style={{ color: RISK_COLORS[m.risk_category] }}
                  data-testid={`merchant-risk-${m.id}`}>{m.risk_score}</div>
              </div>
              <div className="text-center">
                <div className="text-[8px] uppercase tracking-widest text-zinc-500">Earned</div>
                <div className="font-mono text-xs text-zinc-300">{m.coins_earned} BLC</div>
              </div>
              <div className="text-center">
                <div className="text-[8px] uppercase tracking-widest text-zinc-500">Settled</div>
                <div className="font-mono text-xs text-emerald-400">${m.settled_usd.toFixed(2)}</div>
              </div>
              <div className="flex gap-1.5">
                {m.status !== "approved" && (
                  <button data-testid={`merchant-approve-${m.id}`} onClick={() => decide(m, "approve")} disabled={!!busy}
                    className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest bg-emerald-500 text-black px-3 py-2 hover:bg-[#BEF264] transition-colors disabled:opacity-50">
                    {busy === `${m.id}-approve` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve
                  </button>
                )}
                {m.status === "approved" && (
                  <button data-testid={`merchant-suspend-${m.id}`} onClick={() => decide(m, "suspend")} disabled={!!busy}
                    className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest border border-red-500/60 text-red-400 px-3 py-2 hover:bg-red-500/10 transition-colors disabled:opacity-50">
                    <Ban className="w-3 h-3" /> Suspend
                  </button>
                )}
                {m.status === "pending" && (
                  <button data-testid={`merchant-reject-${m.id}`} onClick={() => decide(m, "reject")} disabled={!!busy}
                    className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest border border-zinc-600 text-zinc-400 px-2 py-2 hover:border-red-500/60 hover:text-red-400 transition-colors disabled:opacity-50">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
